import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, X, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import MainLayout from '@/layouts/MainLayout';
import { FiltersBar, FiltersContent } from '@/components/podcasts/FiltersBar';
import { PodcastCard } from '@/components/podcasts/PodcastCard';
import { PodcastTable } from '@/components/podcasts/PodcastTable';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { usePodcastSearch } from '@/hooks/use-podcast-search';
import { SearchingAnimation } from '@/components/ui/searching-animation';
import { Podcast } from '@/types/podcast';
import PodcastAddToCampaignModal from '@/components/podcasts/PodcastAddToCampaignModal';
import { SearchTutorial } from '@/components/podcasts/SearchTutorial';
import { REGION_NAMES, REGION_FLAGS } from '@/utils/region-utils';


export default function PodcastSearchPage() {
    const navigate = useNavigate();

    const [showFilters, setShowFilters] = useState(false);
    const [selectedPodcastForCampaign, setSelectedPodcastForCampaign] = useState<Podcast | null>(null);
    const [showAddToCampaignModal, setShowAddToCampaignModal] = useState(false);

    const handleAddToCampaign = (podcast: Podcast) => {
        setSelectedPodcastForCampaign(podcast);
        setShowAddToCampaignModal(true);
    };

    // Standard Podcast Search Hook (Modified to include episodeKeywords)
    const {
        inputQuery,
        episodeKeywords,
        setEpisodeKeywords,
        filters,
        setFilters,
        sortField,
        setSortField,
        sortOrder,
        setSortOrder,
        page,
        setPage,
        submitSearch,
        podcasts,
        isPending,
        hasMore
    } = usePodcastSearch();

    const [localInput, setLocalInput] = useState(inputQuery);
    const previousKeywordsLengthRef = useRef<number>(0);

    // Auto-search when keywords are added
    useEffect(() => {
        const currentLength = episodeKeywords.length;
        if (currentLength > previousKeywordsLengthRef.current) {
            // Keywords were added, trigger search
            submitSearch(localInput);
        }
        previousKeywordsLengthRef.current = currentLength;
    }, [episodeKeywords, localInput, submitSearch]);

    const handleMainSearch = () => {
         submitSearch(localInput);
    };
    
    const handleMainInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleMainSearch();
        }
    };

    // Update localInput when inputQuery changes
    useEffect(() => {
        setLocalInput(inputQuery);
    }, [inputQuery]);

    const handlePodcastClick = (podcast: Podcast) => {
        const params = new URLSearchParams();
        if (episodeKeywords.length > 0) {
            params.set('episodeKeywords', episodeKeywords.join(','));
        }
        
        navigate(`/dashboard/podcasts/${podcast.id}?${params.toString()}`, {
            state: {
                matchedEpisodes: podcast.matchedEpisodes,
                episodeKeywords
            }
        });
    };

    const activeFilters = useMemo(() => {
        const items: { id: string; label: string; onRemove: () => void }[] = [];

        // Episode Keywords
        if (episodeKeywords && episodeKeywords.length > 0) {
            episodeKeywords.forEach(kw => {
                items.push({
                    id: `kw-${kw}`,
                    label: `Keyword: ${kw}`,
                    onRemove: () => setEpisodeKeywords(episodeKeywords.filter(k => k !== kw))
                });
            });
        }

        // Languages
        if (filters.languages && filters.languages.length > 0) {
            filters.languages.forEach(lang => {
                items.push({
                    id: `lang-${lang}`,
                    label: `Lang: ${lang.toUpperCase()}`,
                    onRemove: () => {
                        setFilters((prev) => ({
                            ...prev,
                            languages: prev.languages?.filter(l => l !== lang)
                        }));
                    }
                });
            });
        }
        
        // Episodes
        if (filters.minEpisodes || filters.maxEpisodes) {
            let label = '';
            if (filters.minEpisodes !== undefined && filters.maxEpisodes !== undefined) {
                label = `${filters.minEpisodes} - ${filters.maxEpisodes} Eps`;
            } else if (filters.minEpisodes !== undefined) {
                label = `> ${filters.minEpisodes} Eps`;
            } else if (filters.maxEpisodes !== undefined) {
                label = `< ${filters.maxEpisodes} Eps`;
            }
            
            items.push({
                id: 'episodes',
                label,
                onRemove: () => setFilters((prev) => ({ ...prev, minEpisodes: undefined, maxEpisodes: undefined }))
            });
        }

        // Ratings
        if (filters.minRating || filters.maxRating) {
            let label = '';
            if (filters.minRating !== undefined && filters.maxRating !== undefined) {
                label = `${filters.minRating} - ${filters.maxRating} ★`;
            } else if (filters.minRating !== undefined) {
                label = `> ${filters.minRating} ★`;
            } else if (filters.maxRating !== undefined) {
                label = `< ${filters.maxRating} ★`;
            }
            
            items.push({
                id: 'rating',
                label,
                onRemove: () => setFilters((prev) => ({ ...prev, minRating: undefined, maxRating: undefined }))
            });
        }

        // Reviews
        if (filters.minRatingCount || filters.maxRatingCount) {
            let label = '';
            if (filters.minRatingCount !== undefined && filters.maxRatingCount !== undefined) {
                label = `${filters.minRatingCount} - ${filters.maxRatingCount} Reviews`;
            } else if (filters.minRatingCount !== undefined) {
                label = `> ${filters.minRatingCount} Reviews`;
            } else if (filters.maxRatingCount !== undefined) {
                label = `< ${filters.maxRatingCount} Reviews`;
            }
            
            items.push({
                id: 'reviews',
                label,
                onRemove: () => setFilters((prev) => ({ ...prev, minRatingCount: undefined, maxRatingCount: undefined }))
            });
        }

        // Listeners
        if (filters.minListeners || filters.maxListeners) {
            let label = '';
            if (filters.minListeners !== undefined && filters.maxListeners !== undefined) {
                label = `${filters.minListeners} - ${filters.maxListeners} Listeners`;
            } else if (filters.minListeners !== undefined) {
                label = `> ${filters.minListeners} Listeners`;
            } else if (filters.maxListeners !== undefined) {
                label = `< ${filters.maxListeners} Listeners`;
            }
            items.push({
                id: 'listeners',
                label,
                onRemove: () => setFilters((prev) => ({ ...prev, minListeners: undefined, maxListeners: undefined }))
            });
        }

        // YouTube Subscribers
        if ((filters as any).minYoutubeSubscriberCount) {
            const val = (filters as any).minYoutubeSubscriberCount;
            const display = val >= 1000000 ? `${val / 1000000}M` : val >= 1000 ? `${val / 1000}k` : val;
            items.push({
                id: 'youtubeSubscribers',
                label: `> ${display} YouTube Subs`,
                onRemove: () => setFilters((prev) => ({ ...prev, minYoutubeSubscriberCount: undefined } as any))
            });
        }

        // Primary Market / Region
        if (filters.primaryMarket) {
            items.push({
                id: 'primaryMarket',
                label: `Region: ${REGION_FLAGS[filters.primaryMarket] || ''} ${REGION_NAMES[filters.primaryMarket] || filters.primaryMarket.toUpperCase()}`,
                onRemove: () => setFilters((prev) => ({ ...prev, primaryMarket: undefined }))
            });
        }

        // Active Within
        if (filters.activeWithinDays) {
            items.push({
                id: 'activeWithin',
                label: `Active < ${filters.activeWithinDays}d`,
                onRemove: () => setFilters((prev) => ({ ...prev, activeWithinDays: undefined }))
            });
        }

        // Has Email
        if (filters.hasEmail) {
            items.push({
                id: 'hasEmail',
                label: 'Has Email',
                onRemove: () => setFilters((prev) => ({ ...prev, hasEmail: false }))
            });
        }

        // Author
        if (filters.author) {
            items.push({
                id: 'author',
                label: `Author: ${filters.author}`,
                onRemove: () => setFilters((prev) => ({ ...prev, author: undefined }))
            });
        }

        // Host
        if (filters.host) {
            items.push({
                id: 'host',
                label: `Host: ${filters.host}`,
                onRemove: () => setFilters((prev) => ({ ...prev, host: undefined }))
            });
        }

        // Explicit
        if (filters.explicit) {
            items.push({
                id: 'explicit',
                label: 'Explicit Only',
                onRemove: () => setFilters((prev) => ({ ...prev, explicit: undefined }))
            });
        }

        // Exclude Explicit
        if (filters.excludeExplicit) {
            items.push({
                id: 'excludeExplicit',
                label: 'No Explicit',
                onRemove: () => setFilters((prev) => ({ ...prev, excludeExplicit: undefined }))
            });
        }

        // Popularity Score
        if (filters.minPopularityScore || filters.maxPopularityScore) {
            let label = '';
            if (filters.minPopularityScore !== undefined && filters.maxPopularityScore !== undefined) {
                label = `Pop: ${filters.minPopularityScore} - ${filters.maxPopularityScore}`;
            } else if (filters.minPopularityScore !== undefined) {
                label = `Pop > ${filters.minPopularityScore}`;
            } else if (filters.maxPopularityScore !== undefined) {
                label = `Pop < ${filters.maxPopularityScore}`;
            }
            items.push({
                id: 'popularity',
                label,
                onRemove: () => setFilters((prev) => ({ ...prev, minPopularityScore: undefined, maxPopularityScore: undefined }))
            });
        }

        // Valid RSS Feed
        if (filters.hasValidRssFeed) {
            items.push({
                id: 'hasValidRssFeed',
                label: 'Valid RSS',
                onRemove: () => setFilters((prev) => ({ ...prev, hasValidRssFeed: false }))
            });
        }

        return items;
    }, [filters, episodeKeywords, setFilters, setEpisodeKeywords]);

    return (
        <MainLayout>
             <div className="flex h-[calc(100vh-64px)]">
                {/* Filters Sidebar - Always visible on desktop */}
                <FiltersBar 
                    filters={filters} 
                    onFilterChange={setFilters}
                    episodeKeywords={episodeKeywords}
                    onEpisodeKeywordsChange={setEpisodeKeywords}
                    sortField={sortField}
                    onSortFieldChange={setSortField}
                    sortOrder={sortOrder}
                    onSortOrderChange={setSortOrder}
                />
                
                {/* Main Content Area */}
                <main className="flex-1 p-2 md:p-3 overflow-y-auto w-full"> 
                    {/* Search Section */}
                    <div className="bg-white rounded-lg border border-border shadow-sm p-3 md:p-4 mb-3">
                        <div className="space-y-3">
                            <div className="flex flex-col gap-2">
                                <div className="flex flex-col sm:flex-row gap-2" data-tour="search-bar">
                                    <div className="flex-1 relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                                        <Input
                                            placeholder="Search by podcast title, author..."
                                            value={localInput}
                                            onChange={(e) => setLocalInput(e.target.value)}
                                            onKeyDown={handleMainInputKeyDown}
                                            className="pl-9 h-10 text-sm bg-slate-50 border-border rounded-md focus-visible:ring-primary w-full"
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <Button 
                                            onClick={handleMainSearch}
                                            className="h-10 flex-1 sm:flex-none px-4 rounded-md shadow-sm shadow-primary/20 bg-primary hover:bg-primary/90 text-sm"
                                        >
                                            Search
                                        </Button>

                                        <Button
                                            variant="outline"
                                            onClick={() => setShowFilters(!showFilters)}
                                            className={`lg:hidden h-10 px-3 gap-2 rounded-md border-border shrink-0 ${showFilters ? 'bg-slate-100 border-primary/50 text-primary' : 'bg-white'}`}
                                        >
                                            <Filter className="size-4" />
                                            <span className="inline text-sm">Filters</span>
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Active Filters Chips */}
                                {activeFilters.length > 0 && (
                                    <div className="flex flex-wrap gap-2 animate-in fade-in slide-in-from-top-1">
                                        {activeFilters.map((filter) => (
                                            <div 
                                                key={filter.id}
                                                className="bg-slate-100 text-slate-800 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 border border-slate-200 group hover:border-primary/40 hover:bg-primary/10 transition-colors"
                                            >
                                                <span>{filter.label}</span>
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        filter.onRemove();
                                                    }}
                                                    className="text-slate-400 hover:text-red-500 transition-colors"
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        ))}
                                        {activeFilters.length > 0 && (
                                            <button 
                                                onClick={() => {
                                                    setFilters({});
                                                    setEpisodeKeywords([]);
                                                }}
                                                className="text-xs text-muted-foreground hover:text-slate-600 underline px-2"
                                            >
                                                Clear all
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Results Area (No Tabs) */}
                            <div className="mt-4 space-y-2" data-tour="results-area">
                                <div id="podcast-results-top" className="sr-only" />
                                {isPending ? (
                                    <SearchingAnimation />
                                ) : podcasts.length === 0 ? (
                                    <div className="text-center py-6 text-muted-foreground text-sm">No podcasts found.</div>
                                ) : (
                                    <div>
                                        {/* Desktop Table View */}
                                        <div className="hidden md:block">
                                            <PodcastTable 
                                                podcasts={podcasts} 
                                                onClick={handlePodcastClick}
                                                onAddToCampaign={handleAddToCampaign}
                                            />
                                        </div>

                                        {/* Mobile Card View */}
                                        <div className="grid gap-2 md:hidden">
                                            {podcasts.map((podcast) => (
                                                <PodcastCard 
                                                    key={podcast.id} 
                                                    podcast={podcast} 
                                                    onClick={() => handlePodcastClick(podcast)}
                                                />
                                            ))}
                                        </div>
                                        
                                        {/* Pagination Controls */}
                                        {(page > 1 || hasMore) && (
                                            <div className="flex justify-between items-center mt-4 pt-2 border-t border-dashed border-border/60">
                                                <Button
                                                    variant="outline"
                                                    onClick={() => {
                                                            setPage(page - 1);
                                                            document.getElementById('podcast-results-top')?.scrollIntoView({ behavior: 'smooth' });
                                                    }}
                                                    disabled={page <= 1 || isPending}
                                                    className="w-20 h-8 text-xs rounded-md"
                                                >
                                                    Previous
                                                </Button>
                                                
                                                <span className="text-xs text-muted-foreground font-medium">
                                                    Page {page}
                                                </span>

                                                <Button
                                                    variant="outline"
                                                    onClick={() => {
                                                        setPage(page + 1);
                                                        document.getElementById('podcast-results-top')?.scrollIntoView({ behavior: 'smooth' });
                                                    }}
                                                    disabled={!hasMore || isPending}
                                                    className="w-20 h-8 text-xs rounded-md"
                                                >
                                                    Next
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </main>

                {/* Mobile Sheet */}
                <div className="lg:hidden">
                    <Sheet open={showFilters} onOpenChange={setShowFilters}>
                        <SheetContent side="right" className="w-full sm:w-[400px]">
                            <SheetHeader>
                                <SheetTitle>Filters & Sort</SheetTitle>
                            </SheetHeader>
                            <div className="py-6 h-[calc(100vh-100px)] overflow-y-auto">
                                <FiltersContent
                                    filters={filters} 
                                    onFilterChange={setFilters}
                                    episodeKeywords={episodeKeywords}
                                    onEpisodeKeywordsChange={setEpisodeKeywords}
                                    sortField={sortField}
                                    onSortFieldChange={setSortField as any}
                                    sortOrder={sortOrder}
                                    onSortOrderChange={setSortOrder}
                                />
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
            {selectedPodcastForCampaign && (
                <PodcastAddToCampaignModal
                    open={showAddToCampaignModal}
                    onClose={() => setShowAddToCampaignModal(false)}
                    podcast={selectedPodcastForCampaign}
                />
            )}
            <SearchTutorial />
        </MainLayout>
    );
}
